import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, FlaskConical, ClipboardList } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SectionLabel from '../components/SectionLabel';

const sections = [
  {
    title: 'Psycholinguistic Experiments',
    icon: FlaskConical,
    description:
      'Paradigms in pragmatics and sentence processing focusing on interpretation, acceptability, and online processing.',
    items: [
      { title: 'Forced-Choice Task', description: 'Participants choose between predefined response options to indicate interpretation preferences, here used to investigate expressive adjectives.', link: 'https://eaperspective.netlify.app/' },
      { title: 'Referent Selection Task', description: 'Participants identify the perceived target of offensiveness by selecting faces.', link: 'https://groupderogmeaning.netlify.app/' },
      { title: 'Likert-Scale Rating', description: 'Participants rate felicity of linguistic stimuli, here applied to wh-exclamatives (delayed response activation).', link: 'https://whexclamatives.netlify.app/' },
      { title: 'Incongruence Detection Task', description: 'Sequential word presentation; participants detect incongruent items to measure processing sensitivity.', link: 'https://pejorativeincondetec.netlify.app/' },
      { title: 'Self-Paced Reading', description: 'Measures processing effort during sentence comprehension, here for gender-related pejoratives.', link: 'https://genderpejorativespr.netlify.app/' },
    ],
  },
  {
    title: 'Survey & Questionnaire Techniques',
    icon: ClipboardList,
    description:
      'Questionnaire-based paradigms focusing on survey logic, routing, and dynamic data collection.',
    items: [
      { title: 'Basic Screening', description: 'Single-criterion eligibility screening.', link: 'https://sosci-basic-screening.netlify.app/' },
      { title: 'Language Selection', description: 'Language-based routing within questionnaires.', link: 'https://sosci-dif-lang.netlify.app/' },
      { title: 'Complex Screening', description: 'Multi-criterion inclusion and exclusion logic.', link: 'https://sosci-complex-screening.netlify.app/' },
      { title: 'Piping', description: 'Dynamic reuse of participant responses.', link: 'https://sosci-piping.netlify.app/' },
      { title: 'Branching', description: 'Conditional questionnaire flow.', link: 'https://sosci-branching.netlify.app/' },
      { title: 'Carry Forward', description: 'Reusing selected answers in later questions.', link: 'https://sosci-carry-forward.netlify.app/' },
      { title: 'Multi-Topic Questionnaire', description: 'Structured survey with multiple thematic sections.', link: 'https://sosci-dif-top.netlify.app/' },
    ],
  },
];

const Experiments = () => (
  <div>
    <PageHeader
      eyebrow="Kalle Glauch"
      title="Experiments"
      lead={
        <>
          Interactive experiments in linguistics and social science, implemented with{' '}
          <a
            href="https://magpie-experiments.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-[color:var(--on-navy)] underline decoration-[#A6C0DB] underline-offset-4 hover:decoration-white"
          >
            magpie
          </a>
          . Each demo shows a different method for designing, implementing, and managing behavioural data collection.
        </>
      }
    />

    <div className="mx-auto max-w-5xl px-6 py-14 md:px-10 md:py-16">
      {sections.map((section, i) => (
        <section key={section.title} className={i > 0 ? 'mt-16 border-t border-line pt-12' : ''}>
          <SectionLabel icon={section.icon}>{section.title}</SectionLabel>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink-600">{section.description}</p>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {section.items.map((item, idx) => (
              <motion.div
                key={item.title}
                className="flex flex-col rounded-md border border-line bg-beige-50 p-6"
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.35, delay: (idx % 2) * 0.05 }}
              >
                <h3 className="text-sm font-semibold text-ink-900">{item.title}</h3>
                <p className="mt-1.5 flex-grow text-sm leading-relaxed text-ink-600">{item.description}</p>
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1.5 self-start rounded-md bg-navy-900 px-4 py-2 text-xs font-medium text-[color:var(--on-navy)] transition-colors hover:bg-navy-800"
                >
                  Run experiment
                  <ExternalLink size={13} />
                </a>
              </motion.div>
            ))}
          </div>
        </section>
      ))}
    </div>
  </div>
);

export default Experiments;
